import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useMemo,
	useRef
} from "react";
import {
	AppIcon,
	FontIconName,
	Icon,
	IconButton,
	ReorderableList
} from "client/Components";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId } from "client/Misc/clientId";
import {
	getItemName,
	GroupItemFormModel,
	IntegrationFlowData,
	ScopeItemFormModel,
	ScopesData
} from "./common";
import { ItemContent } from "./ItemContent";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { ScopeItemMenu } from "./ScopeItemMenu";
import { SearchIntegrationKind, GlobalSearchScopeKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { never } from "client_legacy/ndaw/errors";
import classNames from "classnames";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel } from "client/Api/Apps/common";
import { AppImageDisplayInfo, RenderImageType } from "client/Api/AppIcon/common";
import styles from "./ScopeItem.module.less";
import { useItemMenuState } from "client/Hooks";

export type ScopeItemProps = {
	attachable: IValueInputAttachable<ScopeItemFormModel>;
	groups: readonly GroupItemFormModel[];
	scopesData: ScopesData;
	containingGroupId?: Guid;
	onMoveToGroup: (clientId: Guid, targetGroupId: Guid) => void;
	onMoveToTop?: (clientId: Guid) => void;
};

export const ScopeItem = (props: ScopeItemProps): ReactElement => {
	const {
		attachable,
		containingGroupId,
		groups,
		scopesData,
		onMoveToGroup,
		onMoveToTop
	} = props;

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const name = useMemo(
		(): string => getItemName(
			attachable
				.reducer
				.value,
			scopesData
		),
		[
			attachable.reducer.value,
			scopesData
		]
	);

	const clientId: Guid = getClientId(attachable.value);

	const shouldShowMoveToGroup = useMemo(
		(): boolean => {
			return groups.some(
				x => getClientId(x) !== containingGroupId
			);
		},
		[containingGroupId, groups]
	);

	const hasAnyActions: boolean = shouldShowMoveToGroup
		|| isNotNil(onMoveToTop);

	return (
		<>
			<ReorderableList.RowItem
				trackingId={generateTrackingId("scope-item", `|${clientId}`)}
				actions={hasAnyActions
					? (
						<IconButton
							ref={menuButtonRef}
							icon="ellipsis"
							kind="transparent"
							shape="round"
							trackingId="open-actions"
							onClick={() => onOpenMoreMenu()}
							isActive={menuState.isOpen && !menuState.isContextMenu}
						/>
					)
					: undefined
				}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu
				}}
			>
				<ItemContent
					icon={(
						<ItemIcon
							item={attachable.value}
							integrationsData={scopesData.integrationFlowsData}
						/>
					)}
				>
					{name}
				</ItemContent>
			</ReorderableList.RowItem>
			{hasAnyActions && menuState.isOpen && (
				<ScopeItemMenu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					name={name}
					containingGroupId={containingGroupId}
					groups={groups}
					onMoveToGroup={
						shouldShowMoveToGroup
							? (targetGroupId) => onMoveToGroup(clientId, targetGroupId)
							: undefined
					}
					onMoveToTop={
						onMoveToTop
							? () => onMoveToTop(clientId)
							: undefined
					}
				/>
			)}
		</>
	);
};
ScopeItem.displayName = "ScopeItem";

type ItemIconProps = {
	item: ScopeItemFormModel;
	integrationsData: Map<number, IntegrationFlowData>;
};

const ItemIcon = (props: ItemIconProps): ReactElement => {
	const {
		integrationsData,
		item
	} = props;

	const {
		scopeData: { scope }
	} = item;


	switch(scope.kind) {
		case GlobalSearchScopeKind.Predefined:
			return (
				<Icon
					value={{
						kind: "font",
						color: "primary",
						name: predefinedIntegrationKindToIconNameMap[scope.predefinedKind]
					}}
				/>
			);
		case GlobalSearchScopeKind.IntegrationFlow:
			const data: IntegrationFlowData = ensureNotNil(
				integrationsData.get(scope.integrationId)
			);

			return (
				<IntegrationIcon
					color={data.color}
					imageDisplayInfo={data.imageDisplayInfo}
				/>
			);
		default:
			never(scope);
	}
};
ItemIcon.displayName = "ItemIcon";

type IntegrationIconProps = {
	color: AppColorViewModel;
	imageDisplayInfo: AppImageDisplayInfo;
};

const IntegrationIcon = (props: IntegrationIconProps): ReactElement => {
	const {
		color,
		imageDisplayInfo
	} = props;

	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const {
		renderType
	} = imageDisplayInfo;

	return (
		<div
			className={classNames(
				styles.icon,
				colorClass,
				{
					[styles.icon_raster]: renderType === RenderImageType.Raster,
					[styles.icon_colorizedSvg]: renderType === RenderImageType.ColorizableSvg
				}
			)}
			style={colorStyle}
		>
			<AppIcon
				value={imageDisplayInfo}
				color="inherit"
			/>
		</div>
	);
};
IntegrationIcon.displayName = "IntegrationIcon";

const predefinedIntegrationKindToIconNameMap: Record<SearchIntegrationKind, FontIconName> = {
	[SearchIntegrationKind.Announcements]: "bullhorn",
	[SearchIntegrationKind.Apps]: "grid-2",
	[SearchIntegrationKind.Files]: "file",
	[SearchIntegrationKind.Folders]: "folder-open",
	[SearchIntegrationKind.Emails]: "envelope",
	[SearchIntegrationKind.Events]: "calendar-star",
	[SearchIntegrationKind.KnowledgeItems]: "book",
	[SearchIntegrationKind.Contacts]: "address-book",
	[SearchIntegrationKind.Egnyte]: "egnyte",
	[SearchIntegrationKind.Communities]: "users",
	[SearchIntegrationKind.CommunityPosts]: "users"
};